import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    'Supabase configuration is missing. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in your .env file.'
  );
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Helper function to normalize Czech text (remove diacritics/accents)
// This allows searching without special characters like á, č, ď, é, ě, í, ň, ó, ř, š, ť, ú, ů, ý, ž
export const normalizeText = (text) => {
  if (!text) return '';
  
  // Czech character mapping to their non-accented equivalents
  const czechMap = {
    'á': 'a', 'Á': 'A',
    'č': 'c', 'Č': 'C',
    'ď': 'd', 'Ď': 'D',
    'é': 'e', 'É': 'E',
    'ě': 'e', 'Ě': 'E',
    'í': 'i', 'Í': 'I',
    'ň': 'n', 'Ň': 'N',
    'ó': 'o', 'Ó': 'O',
    'ř': 'r', 'Ř': 'R',
    'š': 's', 'Š': 'S',
    'ť': 't', 'Ť': 'T',
    'ú': 'u', 'Ú': 'U',
    'ů': 'u', 'Ů': 'U',
    'ý': 'y', 'Ý': 'Y',
    'ž': 'z', 'Ž': 'Z',
  };
  
  return text
    .split('')
    .map(char => czechMap[char] || char)
    .join('')
    .toLowerCase();
};

// API functions for playlists and videos
export const playlistsAPI = {
  // Get all playlists/artists
  async getAll() {
    const { data, error } = await supabase
      .from('playlists')
      .select('*, videos(count)')
      .order('name', { ascending: true });

    if (error) throw error;
    
    return data.map((playlist) => ({
      ...playlist,
      videoCount: playlist.videos?.[0]?.count || playlist.videos?.count || 0,
    }));
  },

  // Get a specific playlist by ID
  async getById(id) {
    const { data, error } = await supabase
      .from('playlists')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  // Get videos for a specific playlist
  async getVideos(playlistId) {
    const { data, error } = await supabase
      .from('videos')
      .select('*')
      .eq('playlist_id', playlistId)
      .order('id', { ascending: true });

    if (error) throw error;
    return data;
  },

  // Search videos across all playlists with Czech diacritics support
  async searchVideos(searchTerm) {
    // Fetch all videos with their playlist data
    const { data, error } = await supabase
      .from('videos')
      .select(`
        *,
        playlists:playlist_id (
          id,
          name,
          logo
        )
      `);

    if (error) throw error;
    
    // Filter videos using normalized text comparison for Czech language support
    const normalizedSearchTerm = normalizeText(searchTerm);
    return data.filter(video => 
      normalizeText(video.title).includes(normalizedSearchTerm) ||
      normalizeText(video.artist).includes(normalizedSearchTerm)
    );
  },
};

// API functions for citations (citat)
export const citationsAPI = {
  // Get random citation
  async getRandom() {
    const { data, error } = await supabase
      .from('citat')
      .select('*')
      .limit(1)
      .single();

    if (error) throw error;
    return data;
  },

  // Get all citations
  async getAll() {
    const { data, error } = await supabase
      .from('citat')
      .select('*');

    if (error) throw error;
    return data;
  },
};

export default supabase;
